// Helpers
import { to, stripDashes } from '../../../lib/helpers';
import {
  checkRequestFailure,
  handleRequestFailure,
} from '../../../lib/validation';
import { getAgencyErrors } from '../../../lib/validation/errorsMap/agencies';
import { bodyHasError } from '../../../lib/validation/serverErrors';
import { selectDeviceId } from '../../auth/authSelectors';

// Requests
import {
  preLookupAppLinkRequest,
  getAppParamsRequest,
  verifyAppLinkRequest,
  linkAppLinkRequest,
} from '../../../backend/agencyRequests';
import {
  reverifyVerifyStepUpUserRequest,
  reverifyConfirmAndFinishRequest,
} from '../../../backend/reverifyRequests';

// Actions
import { push } from 'connected-react-router';
import { storeAlertInfo } from '../../alert/alertActions';
import { APP_TRANSACTION_IDS } from '../../app/appActions';
import { getMyApps, storeLink } from '../agencyActions';

// Steps
import { LINK_SERVICES_STEPS } from '../../../pages/LinkServicePage/LinkServiceSteps';
import { openModal } from '../../modal/modalActions';
import * as errorActions from '../../error/errorActions';

// Consts
export const AGENCY_LINK_STORE_DATA = '[AGENCY] Link - Store Data';
export const AGENCY_LINK_CLEAR_DATA = '[AGENCY] Link - Clear Data';
export const AGENCY_RELINK_STORE_DATA = '[AGENCY] ReLink - Store Data';

export const selectAgencyToLink = agency => async (dispatch, getState) => {
  // TODO: What happens when an agency has multiple applications to link to?
  if (agency.appIds.length > 1) return false;

  const {
    agency: { apps },
  } = getState();
  const app = apps[agency.appIds[0]];

  dispatch(storeLinkServiceData({ agency, app }));
  return dispatch(
    push({
      pathname: `/link/${LINK_SERVICES_STEPS.PRELOOKUP}/${app.appSlug}`,
    })
  );
};

export const getPreLookupForLink = appId => async dispatch => {
  const [data, err] = await to(
    dispatch(preLookupAppLinkRequest({ appId, transactionId: 'PROGRAMS' }))
  );
  if (!data.body || err) return Promise.reject(err);
  if (!data.body.status || data.body.status !== 'NOT_APPLICABLE')
    dispatch(storeLinkServiceData({ preLookup: data.body.data }));
  return Promise.resolve([data, err]);
};

export const getAppParamsForLink = ({ app, agency }) => async dispatch => {
  if (app && app.appId) {
    const [{ body }] = await to(dispatch(getAppParamsRequest(app.appId)));
    if (!body) return;
    if (body.data)
      return dispatch(storeLinkServiceData({ app, agency, params: body.data }));
  }
};

export const verifyAppLink = (values, formikActions) => async (
  dispatch,
  getState
) => {
  const {
    link: { app, agency, params, selected },
  } = getState().agency;

  const paramsWithValues = params.map(params => ({
    ...params,
    value: stripDashes(values[params.name].trim()),
  }));

  if (selected) {
    paramsWithValues.push({
      name: 'licenseCode',
      value: selected.license.licenseCode,
    });
  }

  const requestBody = {
    appId: app.appId,
    userKey: '1', // has to be a string/number
    transactionId: APP_TRANSACTION_IDS.BASIC_INFO,
    params: paramsWithValues,
  };

  const [{ response, body }, err] = await to(
    dispatch(verifyAppLinkRequest(requestBody))
  );
  if (err) return Promise.reject(err);

  if (checkRequestFailure({ response, body })) {
    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3011'] })) {
      dispatch(push('/link/ineligible'));
      return Promise.reject();
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3503'] })) {
      dispatch(push('/link/can-not-renew-online'));
      return Promise.reject();
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3700'] })) {
      dispatch(openModal('DpsLinkingLockoutModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_E_0005'] })) {
      dispatch(openModal('DpsDlrLockoutModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_E_0031'] })) {
      dispatch(openModal('DpsGuidMismatchModal'));
      return Promise.reject(body);
    }

    if (
      bodyHasError(body, {
        exactError: 'TXTDMVERR017|||No fee due for vehicle registration renew',
      })
    ) {
      dispatch(push('/link/ineligible'));
      return Promise.reject();
    }

    // PRJ1417943 - Integration Accelerator - Changed from static ERRORS_MAP.AGENCY_ERRORS to dynamic getAgencyErrors()
    const agencyErrors = getAgencyErrors();
    handleRequestFailure(
      body,
      formikActions,
      agencyErrors[agency.agencySlug].APP_ERRORS[app.appSlug].linkService
    );
    return Promise.reject(response);
  }

  dispatch(storeLinkServiceData({ paramsWithValues, linkData: body.data }));

  if (body.message && body.message === 'MGMW_EX_3009') {
    if (app && app.appSlug === 'tpwdas')
      dispatch(push(`/link/already-linked/${app.appSlug}`));
    else dispatch(push('/link/already-linked'));
    return Promise.reject();
  }

  if (
    body.data &&
    body.data.eligibleForLinking !== null &&
    body.data.eligibleForLinking === false
  ) {
    const assetType = body.data.assetType;
    formikActions.setStatus({
      serverErrors: {
        formError: {
          id: 'Exception_AssetCantLink',
          title: 'Exception_AssetCantLinkTitle_TPWD',
          type: 'danger',
          assetType: assetType,
        },
      },
    });
    formikActions.setSubmitting(false);
    return Promise.reject();
  }

  return Promise.resolve(body.data);
};

export const verifyStepUpIdentity = (values, formikActions) => async (
  dispatch,
  getState
) => {
  const {
    agency: {
      link: { app, agency, user = {} },
    },
    user: { profile = {} },
  } = getState();

  // Link Service step-up identity (no Texas DL account path) must call
  // /user/reverify/stepUp with isNoTxDLAccount=true.
  const requestBody = {
    phoneNum: user.phoneNumToken ? user.phoneNum || '' : '',
    ...(user.phoneNumToken && { phoneNumToken: user.phoneNumToken }),
    fName: profile.fName || '',
    lName: profile.lName || '',
    userDeviceToken: selectDeviceId(getState()) || '',
    isNoTxDLAccount: true,
    pii: {
      dob: values.dob || '',
      ssn: stripDashes(values.ssn || values.last4ssn || ''),
      dlNumber: values.dlIDNumber || values.dlNumber || '',
      auditNum: values.auditNumber || values.auditNum || '',
      address: {},
    },
  };

  const [{ response, body }, err] = await to(
    dispatch(
      reverifyVerifyStepUpUserRequest(requestBody, {
        navigateOnClose: false,
      })
    )
  );
  if (err) return Promise.reject(err);

  // Matches reverifyActions.verifyUser: guard server-level exception before field-level error handling.
  if (bodyHasError(body, { errorCodes: ['MGMW_EX_0000'] })) {
    return Promise.reject(body);
  }

  // Matches reverifyActions.verifyUser: normalize raw INVALID/ERROR statuses that have no error codes.
  if (['INVALID', 'ERROR'].includes(body.status) && !bodyHasError(body)) {
    const errorId = response.headersObject['x-req-id'];
    body.errors = {
      ...(errorId && { REF: [errorId] }),
      ERROR: ['CREATE_ACCOUNT_ERROR'],
    };
  }

  if (checkRequestFailure({ response, body })) {
    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3007'] })) {
      // Keep this as a form-level warning (yellow alert) on Step Up Identity.
      handleRequestFailure(body, formikActions, {});
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3700'] })) {
      dispatch(openModal('DpsLinkingLockoutModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_E_0005'] })) {
      dispatch(openModal('DpsDlrLockoutModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_E_0031'] })) {
      dispatch(openModal('DpsGuidMismatchModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_EX_4001'] })) {
      dispatch({
        type: errorActions.SERVER_ERROR,
        payload: {
          body,
          response,
          options: { extraOptions: { navigateOnClose: false } },
        },
      });
      return Promise.reject(body);
    }

    handleRequestFailure(
      body,
      formikActions,
      ERRORS_MAP.AGENCY_ERRORS[agency.agencySlug].APP_ERRORS[app.appSlug]
        .linkService
    );

    return Promise.reject(body || { status: response && response.status });
  }

  // Extract verifyToken from response message (will be passed to confirm API later)
  const linkDataWithToken = {
    ...body.data,
    verifyToken: body.message,
  };

  dispatch(storeLinkServiceData({ linkData: linkDataWithToken }));
  return Promise.resolve(linkDataWithToken);
};

export const getAppParamsForLinkOnBoarding = ({
  app = {
    agencyId: '10',
    appCode: 'DLR',
    appId: '9',
    appName: 'Driver License and ID Services',
    appSlug: 'dpslr',
    linkUserKeys: [0],
    links: [0],
  },
  agency = {
    agencyCode: '405',
    agencyId: '10',
    agencyName: 'Texas Department of Public Safety',
    agencySlug: 'dps',
    appIds: '9',
    apps: [0],
    linkUserKeys: [0],
    links: [0],
  },
}) => async dispatch => {
  const [{ body }] = await to(dispatch(getAppParamsRequest('9')));
  if (!body) return;

  if (body.data) {
    dispatch(storeLinkServiceData({ app, agency, params: body.data }));
    return body.data;
  }
};

export const verifyAppLinkOnBoarding = values => async dispatch => {
  const app = {
    agencyId: '10',
    appCode: 'DLR',
    appId: '9',
    appName: 'Driver License and ID Services',
    appSlug: 'dpslr',
    linkUserKeys: [0],
    links: [0],
  };
  const agency = {
    agencyCode: '405',
    agencyId: '10',
    agencyName: 'Texas Department of Public Safety',
    agencySlug: 'dps',
    appIds: '9',
    apps: [0],
    linkUserKeys: [0],
    links: [0],
  };

  const paramsOnBoarding = await to(
    dispatch(getAppParamsForLinkOnBoarding({ app, agency }))
  );

  const paramsWithValues = paramsOnBoarding[0].map(params => ({
    ...params,
    value: values[params.name] ? values[params.name].trim() : '',
  }));

  const requestBody = {
    appId: '9',
    userKey: '1', // has to be a string/number
    transactionId: APP_TRANSACTION_IDS.BASIC_INFO,
    params: paramsWithValues,
  };

  const [{ response, body }, err] = await to(
    dispatch(verifyAppLinkRequest(requestBody))
  );

  if (err) return Promise.reject(err);

  if (checkRequestFailure({ response, body })) {
    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3011'] })) {
      dispatch(push('/link/ineligible'));
      return Promise.reject();
    }

    // Changed from static ERRORS_MAP.AGENCY_ERRORS to dynamic getAgencyErrors()
    const agencyErrors = getAgencyErrors();
    handleRequestFailure(
      body,
      {
        setFormikState: () => {},
        setSubmitting: () => {},
      },
      agencyErrors['dps'].APP_ERRORS['dps'].linkService
    );
    return Promise.reject(body);
  }

  dispatch(storeLinkServiceData({ paramsWithValues, linkData: body.data }));

  if (body.message && body.message === 'MGMW_EX_3009') {
    dispatch(push('/link/already-linked'));
    return Promise.reject();
  }

  return Promise.resolve(body.data);
};

export const linkAppLink = () => async (dispatch, getState) => {
  const {
    link: {
      app: { ...app },
      paramsWithValues,
    },
  } = getState().agency;

  const requestBody = {
    appId: app.appId,
    params: paramsWithValues,
  };

  const [{ response, body }, err] = await to(
    dispatch(linkAppLinkRequest(requestBody))
  );
  if (err || response.status !== 200) {
    if (
      bodyHasError(body, {
        exactError: 'MGMW_EX_3009|||Account already exists',
      })
    ) {
      if (app && app.appSlug === 'tpwdas') {
        dispatch(push(`/link/already-linked/${app.appSlug}`));
      } else {
        dispatch(push('/link/already-linked'));
      }
      return Promise.reject();
    }

    if (
      bodyHasError(body, {
        exactError: 'MGMW_EX_3301|||Entity Id is linked to different account',
      })
    )
      dispatch(
        storeAlertInfo({
          type: 'danger',
          title: { file: 'Alerts', id: 'SorryTitle' },
          message: { file: 'Alerts', id: 'LinkedAnotherAccount' },
          closeable: true,
        })
      );

    return Promise.reject(err || response);
  }

  const link = { ...app, ...body.data };

  dispatch(storeLink(link));

  return Promise.resolve(link);
};

export const linkServiceSuccess = appSlug => dispatch => {
  dispatch(storeLinkServiceData({ success: true }));
  if (appSlug) {
    return dispatch(push(`/link/${LINK_SERVICES_STEPS.SUCCESS}/${appSlug}`));
  } else {
    return dispatch(push(`/link/${LINK_SERVICES_STEPS.SUCCESS}`));
  }
};

export const confirmStepUpAndFinish = (formikActions, appSlug) => async (
  dispatch,
  getState
) => {
  const {
    agency: {
      link: { app = {}, agency = {}, linkData = {} },
    },
  } = getState();

  const { verifyToken } = linkData;

  if (!verifyToken) {
    return Promise.reject(
      new Error('Missing verify token for step-up confirmation')
    );
  }

  const confirmPayload = {
    userSessionToken: verifyToken,
  };

  const extraOptions = {
    navigateOnClose: false,
  };

  const [{ response, body }, err] = await to(
    dispatch(reverifyConfirmAndFinishRequest(confirmPayload, extraOptions))
  );

  if (err) {
    return Promise.reject(err);
  }

  if (bodyHasError(body, { errorCodes: ['MGMW_EX_0000'] })) {
    return Promise.reject(body);
  }

  if (checkRequestFailure({ response, body })) {
    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3700'] })) {
      dispatch(openModal('DpsLinkingLockoutModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_E_0005'] })) {
      dispatch(openModal('DpsDlrLockoutModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_E_0031'] })) {
      dispatch(push(`/link/${LINK_SERVICES_STEPS.ID}/${appSlug}`));
      dispatch(openModal('DpsGuidMismatchModal'));
      return Promise.reject(body);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_EX_4001'] })) {
      dispatch({
        type: errorActions.SERVER_ERROR,
        payload: {
          body,
          response,
          options: { extraOptions: { navigateOnClose: false } },
        },
      });
      return Promise.reject(body);
    }

    return handleRequestFailure(body, formikActions);
  }

  // Keep agency links in sync before navigating to DPS page.
  // Local mock /agency/getMyApps is static and may not immediately reflect new link,
  // so we add the new link to Redux as a fallback to prevent redirecting back to /link.
  let hasDpsLinks = false;
  try {
    await dispatch(getMyApps());
    const refreshedAgency = getState().agency.agencies[agency.agencyId] || {};
    hasDpsLinks =
      Array.isArray(refreshedAgency.linkUserKeys) &&
      refreshedAgency.linkUserKeys.length > 0;
  } catch {
    hasDpsLinks = false;
  }

  if (!hasDpsLinks && app.appId && agency.agencyId) {
    dispatch(
      storeLink({
        agencyId: agency.agencyId,
        agencyName: agency.agencyName,
        appCode: app.appCode,
        appId: app.appId,
        appName: app.appName,
        userKey: linkData.userKey || verifyToken || `${app.appId}-step-up-link`,
      })
    );
  }

  // AC2: Navigate to Step Up Success screen on successful confirmation
  dispatch(storeLinkServiceData({ success: true }));
  return dispatch(
    push(`/link/${LINK_SERVICES_STEPS.STEP_UP_SUCCESS}/${appSlug}`)
  );
};

export const storeLinkServiceData = data => dispatch =>
  dispatch({ type: AGENCY_LINK_STORE_DATA, payload: data });

export const storeReLinkServiceData = data => dispatch =>
  dispatch({ type: AGENCY_RELINK_STORE_DATA, payload: data });

export const clearLinkServiceData = () => dispatch =>
  dispatch({ type: AGENCY_LINK_CLEAR_DATA });
